/**
 * Customer Case Creation Form
 * Manages complete customer order lifecycle from inquiry to payment
 * Parent case that can contain child cases (supplier quotes, order workflow)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Chip,
  Grid,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as SupplierIcon,
  Timeline as WorkflowIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { caseManagementService } from '../../../services/caseManagementService';
import { EmailContentParser, type ParsedEmailContent } from '../../../services/emailContentParser';
import ModernSupplierCaseCreationForm from '../supplier/ModernSupplierCaseCreationForm';
import { formatDate } from '../../../utils/dateUtils';

// Types for Customer Case Management
interface CustomerOrderPosition {
  position_id: string;
  position_number: number;
  part_name: string;
  material: string;
  quantity: number;
  specifications?: string;
  dimensional_drawing?: File;
  dimensional_drawing_path?: string;
  estimated_price?: number;
}

interface WorkflowStage {
  stage: 'initial_contact' | 'quote_request' | 'supplier_quotes' | 'quote_comparison' | 
         'quote_sent' | 'quote_response' | 'order_placement' | 'production' | 
         'delivery' | 'invoicing' | 'payment' | 'closed';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  completed_at?: string;
  notes?: string;
}

interface CustomerCaseFormData {
  customer_email: string;
  customer_name: string;
  customer_company: string;
  title: string;
  description?: string;
  priority: 1 | 2 | 3 | 4 | 5;
  positions: CustomerOrderPosition[];
  workflow_stages: WorkflowStage[];
  related_email_id?: string;
  auto_reply_enabled: boolean;
  quote_deadline?: string;
  // Child case management
  supplier_cases: string[]; // IDs of supplier quote cases
  order_workflow_case?: string; // ID of order workflow case
}

interface CustomerCaseCreationFormProps {
  open: boolean;
  onClose: () => void;
  onCaseCreated: (caseData: any) => void;
  initialData?: Partial<CustomerCaseFormData>;
  emailContext?: {
    email_id: string;
    from_address: string;
    subject: string;
    body_text?: string;
    attachments?: any[];
  };
}

const defaultPosition: CustomerOrderPosition = {
  position_id: '',
  position_number: 1,
  part_name: '',
  material: '',
  quantity: 0,
  specifications: ''
};

const defaultWorkflowStages: WorkflowStage[] = [
  { stage: 'initial_contact', status: 'completed' },
  { stage: 'quote_request', status: 'pending' },
  { stage: 'supplier_quotes', status: 'pending' },
  { stage: 'quote_comparison', status: 'pending' },
  { stage: 'quote_sent', status: 'pending' },
  { stage: 'quote_response', status: 'pending' },
  { stage: 'order_placement', status: 'pending' },
  { stage: 'production', status: 'pending' },
  { stage: 'delivery', status: 'pending' },
  { stage: 'invoicing', status: 'pending' },
  { stage: 'payment', status: 'pending' },
  { stage: 'closed', status: 'pending' }
];

export default function CustomerCaseCreationForm({
  open,
  onClose,
  onCaseCreated,
  initialData,
  emailContext
}: CustomerCaseCreationFormProps) {
  const [formData, setFormData] = useState<CustomerCaseFormData>({
    customer_email: '',
    customer_name: '',
    customer_company: '',
    title: '',
    description: '',
    priority: 3,
    positions: [{ ...defaultPosition, position_id: 'POS-001', position_number: 1 }],
    workflow_stages: [...defaultWorkflowStages],
    auto_reply_enabled: true,
    quote_deadline: '',
    supplier_cases: []
  });

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [supplierFormOpen, setSupplierFormOpen] = useState(false);
  
  // Email parsing state
  const [parsedEmailData, setParsedEmailData] = useState<ParsedEmailContent | null>(null);
  const [showParsePreview, setShowParsePreview] = useState(false);
  const [parsingEmail, setParsingEmail] = useState(false);
  
  // Auto-fill suggestions state
  const [previousPartNames, setPreviousPartNames] = useState<string[]>([]);
  const [previousMaterials, setPreviousMaterials] = useState<string[]>([]);
  const [previousCompanies, setPreviousCompanies] = useState<string[]>([]);
  
  const { showNotification } = useNotification();

  const steps = [
    'Customer Information',
    'Order Details',
    'Review & Create'
  ];

  // Initialize form with email context or initial data
  useEffect(() => {
    if (emailContext) {
      setFormData(prev => ({
        ...prev,
        customer_email: emailContext.from_address,
        title: emailContext.subject || 'Customer Order Request',
        description: emailContext.body_text || '',
        related_email_id: emailContext.email_id
      }));
    } else if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [emailContext, initialData]);

  // Load previous values from localStorage for auto-fill suggestions
  useEffect(() => {
    const savedPartNames = localStorage.getItem('wzf_previous_part_names');
    const savedMaterials = localStorage.getItem('wzf_previous_materials');
    const savedCompanies = localStorage.getItem('wzf_previous_companies');
    
    if (savedPartNames) {
      setPreviousPartNames(JSON.parse(savedPartNames));
    }
    
    if (savedMaterials) {
      setPreviousMaterials(JSON.parse(savedMaterials));
    }
    
    if (savedCompanies) {
      setPreviousCompanies(JSON.parse(savedCompanies));
    }
  }, []);

  // Form validation
  const validateStep = (stepIndex: number): boolean => {
    const errors: Record<string, string> = {};
    
    switch (stepIndex) {
      case 0: // Customer Information
        if (!formData.customer_email) errors.customer_email = 'Email ist erforderlich';
        if (!formData.customer_company) errors.customer_company = 'Unternehmen ist erforderlich';
        break;
      case 1: // Order Details
        if (!formData.title) errors.title = 'Titel ist erforderlich';
        if (formData.positions.length === 0) errors.positions = 'Mindestens eine Position erforderlich';
        formData.positions.forEach((pos, index) => {
          if (!pos.part_name) errors[`position_${index}_part_name`] = 'Teilename erforderlich';
          if (!pos.material) errors[`position_${index}_material`] = 'Material erforderlich';
          if (!pos.quantity || pos.quantity <= 0) errors[`position_${index}_quantity`] = 'Bitte Menge eingeben (min. 1)';
        });
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form updates
  const updateFormData = (field: keyof CustomerCaseFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Save to localStorage for auto-fill suggestions
    if (field === 'customer_company' && value && !previousCompanies.includes(value)) {
      const updatedCompanies = [...previousCompanies, value];
      setPreviousCompanies(updatedCompanies);
      localStorage.setItem('wzf_previous_companies', JSON.stringify(updatedCompanies));
    }
  };

  // Helper function to save part names and materials
  const saveToAutoFill = (field: 'part_name' | 'material', value: string) => {
    if (!value) return;
    
    if (field === 'part_name' && !previousPartNames.includes(value)) {
      const updatedPartNames = [...previousPartNames, value];
      setPreviousPartNames(updatedPartNames);
      localStorage.setItem('wzf_previous_part_names', JSON.stringify(updatedPartNames));
    } else if (field === 'material' && !previousMaterials.includes(value)) {
      const updatedMaterials = [...previousMaterials, value];
      setPreviousMaterials(updatedMaterials);
      localStorage.setItem('wzf_previous_materials', JSON.stringify(updatedMaterials));
    }
  };

  // Position management
  const addPosition = () => {
    const newPosition: CustomerOrderPosition = {
      ...defaultPosition,
      position_id: `POS-${String(formData.positions.length + 1).padStart(3, '0')}`,
      position_number: formData.positions.length + 1
    };
    updateFormData('positions', [...formData.positions, newPosition]);
  };

  const updatePosition = (index: number, field: keyof CustomerOrderPosition, value: any) => {
    const updatedPositions = formData.positions.map((pos, i) =>
      i === index ? { ...pos, [field]: value } : pos
    );
    updateFormData('positions', updatedPositions);
    
    // Save to auto-fill suggestions
    if (field === 'part_name' || field === 'material') {
      saveToAutoFill(field, value);
    }
  };

  const removePosition = (index: number) => {
    if (formData.positions.length > 1) {
      const updatedPositions = formData.positions.filter((_, i) => i !== index);
      updateFormData('positions', updatedPositions);
    }
  };

  // Email parsing functionality
  const handleParseEmailContent = async () => {
    if (!emailContext) {
      showNotification('No email context available for parsing', 'warning');
      return;
    }

    setParsingEmail(true);
    try {
      // Parse email content
      const parsedData = EmailContentParser.parseCustomerRequest({
        from_address: emailContext.from_address,
        subject: emailContext.subject,
        body_text: emailContext.body_text,
        body_html: undefined // We'll use body_text for now
      });

      setParsedEmailData(parsedData);
      setShowParsePreview(true);
      
      showNotification(
        `Email parsed successfully! Found ${parsedData.positions.length} positions with ${parsedData.extractionMetadata.confidence} confidence.`,
        'success'
      );
    } catch (error) {
      console.error('Error parsing email:', error);
      showNotification('Error parsing email content', 'error');
    } finally {
      setParsingEmail(false);
    }
  };

  const applyParsedData = () => {
    if (!parsedEmailData || !emailContext) return;

    // Convert parsed data to form format
    const formDataFromEmail = EmailContentParser.convertToFormData(parsedEmailData, emailContext);
    
    // Apply to form, preserving existing data where preferred
    setFormData(prev => ({
      ...prev,
      customer_email: formDataFromEmail.customer_email || prev.customer_email,
      customer_name: formDataFromEmail.customer_name || prev.customer_name,
      customer_company: formDataFromEmail.customer_company || prev.customer_company,
      title: formDataFromEmail.title || prev.title,
      description: formDataFromEmail.description || prev.description,
      priority: formDataFromEmail.priority || prev.priority,
      quote_deadline: formDataFromEmail.quote_deadline || prev.quote_deadline,
      positions: formDataFromEmail.positions || prev.positions,
      related_email_id: formDataFromEmail.related_email_id || prev.related_email_id
    }));

    setShowParsePreview(false);
    showNotification('Email data applied to form successfully!', 'success');
  };

  // Form submission
  const handleSubmit = async () => {
    if (!validateStep(step)) {
      return;
    }

    if (step < steps.length - 1) {
      setStep(step + 1);
      return;
    }

    setLoading(true);
    try {
      // Use new customer case API
      const customerCaseData = {
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        customer_company: formData.customer_company,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        quote_deadline: formData.quote_deadline,
        positions: formData.positions.map(pos => ({
          part_name: pos.part_name,
          part_number: pos.position_id, // Use position_id as part_number for now
          material: pos.material,
          quantity: pos.quantity,
          specifications: pos.specifications, // Backend expects 'specifications', not 'notes'
          dimensional_drawing: pos.dimensional_drawing, // Pass the File object directly
          supplier_quote_type: 'general' // Default type
        })),
        related_email_id: formData.related_email_id
      };

      const response = await caseManagementService.createCustomerCase(customerCaseData);
      
      if (response.success) {
        showNotification('Customer Case erfolgreich erstellt!', 'success');
        onCaseCreated(response.data);
        
        // Reset form
        setFormData({
          customer_email: '',
          customer_name: '',
          customer_company: '',
          title: '',
          description: '',
          priority: 3,
          positions: [{ ...defaultPosition, position_id: 'POS-001', position_number: 1 }],
          workflow_stages: [...defaultWorkflowStages],
          auto_reply_enabled: true,
          quote_deadline: '',
          supplier_cases: []
        });
        setStep(0);
        onClose();
      } else {
        showNotification(`Fehler: ${response.message}`, 'error');
      }
    } catch (error: any) {
      showNotification(`Fehler: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierCaseCreated = (supplierCase: any) => {
    setSupplierFormOpen(false);
    updateFormData('supplier_cases', [...formData.supplier_cases, supplierCase.id]);
    showNotification(`Supplier Case ${supplierCase.case_number} erstellt!`, 'success');
  };

  const renderStepContent = () => {
    switch (step) {
      case 0: // Customer Information
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              
              {/* Email Parsing Section */}
              {emailContext && (
                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <BusinessIcon color="primary" />
                      <Typography variant="subtitle1" color="primary">
                        Smart Email Analysis Available
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      We can automatically extract customer information and order positions from the email content.
                      This will auto-populate the form fields below.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        startIcon={<BusinessIcon />}
                        onClick={handleParseEmailContent}
                        disabled={parsingEmail}
                        color="primary"
                      >
                        {parsingEmail ? 'Analyzing Email...' : 'Parse Email Content'}
                      </Button>
                      
                      {parsedEmailData && (
                        <Chip
                          label={`${parsedEmailData.positions.length} positions found`}
                          color={parsedEmailData.extractionMetadata.confidence === 'high' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-Mail Address *"
                value={formData.customer_email}
                onChange={(e) => updateFormData('customer_email', e.target.value)}
                error={!!validationErrors.customer_email}
                helperText={validationErrors.customer_email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.customer_name}
                onChange={(e) => updateFormData('customer_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={previousCompanies}
                value={formData.customer_company}
                onInputChange={(_, value) => updateFormData('customer_company', value || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Company *"
                    error={!!validationErrors.customer_company}
                    helperText={validationErrors.customer_company || 'Start typing to see suggestions from previous entries'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => updateFormData('priority', e.target.value as number)}
                >
                  <MenuItem value={1}>Very High</MenuItem>
                  <MenuItem value={2}>High</MenuItem>
                  <MenuItem value={3}>Normal</MenuItem>
                  <MenuItem value={4}>Low</MenuItem>
                  <MenuItem value={5}>Very Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_reply_enabled}
                    onChange={(e) => updateFormData('auto_reply_enabled', e.target.checked)}
                  />
                }
                label="Enable Auto-Reply"
              />
            </Grid>
          </Grid>
        );

      case 1: // Order Details
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Order Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Case Title *"
                  value={formData.title}
                  onChange={(e) => updateFormData('title', e.target.value)}
                  error={!!validationErrors.title}
                  helperText={validationErrors.title}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Order Positions</Typography>
                <Button startIcon={<AddIcon />} onClick={addPosition}>
                  Add Position
                </Button>
              </Box>

              {formData.positions.map((position, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">
                        Position {position.position_number}
                      </Typography>
                      {formData.positions.length > 1 && (
                        <IconButton 
                          onClick={() => removePosition(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          freeSolo
                          options={previousPartNames}
                          value={position.part_name}
                          onInputChange={(_, value) => updatePosition(index, 'part_name', value || '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="Part Name *"
                              error={!!validationErrors[`position_${index}_part_name`]}
                              helperText={validationErrors[`position_${index}_part_name`] || 'Start typing to see suggestions'}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          freeSolo
                          options={previousMaterials}
                          value={position.material}
                          onInputChange={(_, value) => updatePosition(index, 'material', value || '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="Material *"
                              error={!!validationErrors[`position_${index}_material`]}
                              helperText={validationErrors[`position_${index}_material`] || 'Start typing to see suggestions'}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Menge *"
                          placeholder="z.B. 10"
                          value={position.quantity === 0 ? '' : position.quantity}
                          onChange={(e) => updatePosition(index, 'quantity', parseInt(e.target.value) || 0)}
                          error={!!validationErrors[`position_${index}_quantity`]}
                          helperText={validationErrors[`position_${index}_quantity`]}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Estimated Price (€)"
                          value={position.estimated_price || ''}
                          onChange={(e) => updatePosition(index, 'estimated_price', parseFloat(e.target.value) || undefined)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          component="label"
                        >
                          Upload Drawing
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                updatePosition(index, 'dimensional_drawing', file);
                              }
                            }}
                          />
                        </Button>
                        {(position.dimensional_drawing || position.dimensional_drawing_path) && (
                          <Typography variant="caption" color="success.main">
                            File: {position.dimensional_drawing?.name || position.dimensional_drawing_path}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Specifications"
                          value={position.specifications}
                          onChange={(e) => updatePosition(index, 'specifications', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        );

      case 2: // Review & Create
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Create
            </Typography>
            
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Customer Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography><strong>E-Mail:</strong> {formData.customer_email}</Typography>
                <Typography><strong>Company:</strong> {formData.customer_company}</Typography>
                {formData.customer_name && (
                  <Typography><strong>Contact:</strong> {formData.customer_name}</Typography>
                )}
                <Typography><strong>Priority:</strong> {formData.priority}</Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Order Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography><strong>Title:</strong> {formData.title}</Typography>
                {formData.description && (
                  <Typography><strong>Description:</strong> {formData.description}</Typography>
                )}
                <Typography><strong>Positions:</strong> {formData.positions.length}</Typography>
                {formData.positions.map((position, index) => (
                  <Box key={index} sx={{ ml: 2, mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Pos {position.position_number}:</strong> {position.part_name} ({position.quantity}x)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Material: {position.material}
                    </Typography>
                    {position.specifications && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Specifications: {position.specifications}
                      </Typography>
                    )}
                    {(position.dimensional_drawing || position.dimensional_drawing_path) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Uploaded Document: {position.dimensional_drawing?.name || position.dimensional_drawing_path}
                      </Typography>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Workflow Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography><strong>Supplier Cases:</strong> {formData.supplier_cases.length}</Typography>
                <Typography><strong>Auto-Reply:</strong> {formData.auto_reply_enabled ? 'Enabled' : 'Disabled'}</Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon />
          <Typography variant="h6">Create Customer Case</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {step > 0 && (
          <Button onClick={() => setStep(step - 1)}>
            Back
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (validateStep(step)) {
                setStep(step + 1);
              }
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? null : <CheckCircleIcon />}
          >
            {loading ? 'Creating Case...' : 'Create Case'}
          </Button>
        )}
      </DialogActions>

      {/* Modern Supplier Case Creation Form */}
      <ModernSupplierCaseCreationForm
        open={supplierFormOpen}
        onClose={() => setSupplierFormOpen(false)}
        onCaseCreated={handleSupplierCaseCreated}
        initialCaseSubType="quote_request"
      />

      {/* Email Parsing Preview Dialog */}
      <Dialog 
        open={showParsePreview} 
        onClose={() => setShowParsePreview(false)}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Email Analysis Results
            {parsedEmailData && (
              <Chip
                label={`${parsedEmailData.extractionMetadata.confidence} confidence`}
                color={parsedEmailData.extractionMetadata.confidence === 'high' ? 'success' : 'warning'}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {parsedEmailData && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review the automatically extracted information below, then click "Apply to Form" to use this data.
              </Typography>

              {/* Customer Information Preview */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Customer Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Company:</Typography>
                      <Typography variant="body1">
                        {parsedEmailData.customerInfo.customer_company || 'Not detected'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Contact Person:</Typography>
                      <Typography variant="body1">
                        {parsedEmailData.customerInfo.customer_name || 'Not detected'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body1">
                        {parsedEmailData.customerInfo.customer_email}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Positions Preview */}
              <Accordion defaultExpanded sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Detected Positions ({parsedEmailData.positions.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {parsedEmailData.positions.length > 0 ? (
                    parsedEmailData.positions.map((position, index) => (
                      <Card key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Part Name:</Typography>
                            <Typography variant="body1">{position.part_name}</Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                            <Typography variant="body1">{position.quantity || 'Not detected'}</Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary">Position #:</Typography>
                            <Typography variant="body1">{position.position_number}</Typography>
                          </Grid>
                          {position.material && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Material:</Typography>
                              <Typography variant="body1">{position.material}</Typography>
                            </Grid>
                          )}
                          {position.specifications && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">Specifications:</Typography>
                              <Typography variant="body2">{position.specifications}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No positions were automatically detected. You can still create them manually.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Metadata */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Extracted {parsedEmailData.extractionMetadata.extractedFields.length} fields • 
                  Language: {parsedEmailData.extractionMetadata.detectedLanguage.toUpperCase()} • 
                  Confidence: {parsedEmailData.extractionMetadata.confidence}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowParsePreview(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={applyParsedData}
            disabled={!parsedEmailData}
          >
            Apply to Form
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
